import { useEffect, useState, useMemo, useRef } from 'react';
import { Box, Typography, IconButton, Stack, TableSortLabel, Tooltip, Chip } from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronRight from '@mui/icons-material/ChevronRight';
import AccountCircle from '@mui/icons-material/AccountCircle';
import * as d3 from 'd3';
import { Link, useLocation } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';

const buildHierarchy = (data, expandedMap, sortConfig) => {
  const root = d3.hierarchy(data);

  root.each((d) => {
    d.expanded = expandedMap[d.data.name] ?? false;
  });

  if (sortConfig.key) {
    root.sort((a, b) => {
      const aVal = Number(a.data[sortConfig.key] ?? 0);
      const bVal = Number(b.data[sortConfig.key] ?? 0);
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }

  return root;
};

const flattenVisibleNodes = (root, expandedMap) => {
  const out = [];

  const walk = (node, depth = 0, visible = true) => {
    const isExpanded = expandedMap[node.data.name] ?? false;
    const isVisible = visible;

    out.push({ ...node, depth, isVisible });

    if (isExpanded && node.children) {
      node.children.forEach((child) => walk(child, depth + 1, isVisible));
    }
  };

  walk(root);
  return out;
};

const StatusReportsTree = ({ data, expandAll }) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const rowHeight = isMobile ? 64 : 52;
  const indent = isMobile ? 2 : 4;
  const lineIndent = isMobile ? 2 : 4;

  const [expandedMap, setExpandedMap] = useState({ [data.name]: true });

  const [columns, setColumns] = useState([
    { key: 'contractStatus', label: 'Contract Status', width: 150 },
  ]);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc',
  });

  const handleSort = (colKey) => {
    setSortConfig((prev) => {
      if (prev.key === colKey) {
        return {
          key: colKey,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key: colKey, direction: 'asc' };
    });
  };

  const containerRef = useRef(null);

  const treeData = useMemo(
    () => buildHierarchy(data, expandedMap, sortConfig),
    [data, expandedMap, sortConfig]
  );

  const visibleNodes = useMemo(
    () => flattenVisibleNodes(treeData, expandedMap),
    [treeData, expandedMap]
  );

  const toggleNode = (node) => {
    setExpandedMap((prev) => ({
      ...prev,
      [node.data.name]: !prev[node.data.name],
    }));
  };

  useEffect(() => {
    const newMap = {};

    const walk = (node) => {
      const isRoot = node.depth === 0;
      newMap[node.data.name] = isRoot || expandAll;
      if (node.children) {
        node.children.forEach(walk);
      }
    };

    const root = d3.hierarchy(data);
    walk(root);
    setExpandedMap(newMap);
  }, [expandAll, data]);

  return (
    <Box sx={{ overflow: 'scroll' }}>
      <Box ref={containerRef} sx={{ position: 'relative', minWidth: 700 }}>
        <Stack direction="row" sx={{ mb: 1, position: 'relative', zIndex: 1, px: 1 }}>
          <Box sx={{ flexGrow: 1, pl: 4 }}>
            <Typography variant="caption">Name</Typography>
          </Box>

          {columns.map((col, idx) => (
            <Box
              key={col.key}
              sx={{
                width: col.width,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                position: 'relative',
                userSelect: 'none',
                cursor: 'move',
                padding: '0 .5rem',
              }}
            >
              <TableSortLabel
                active={sortConfig.key === col.key}
                direction={sortConfig.direction}
                onClick={() => handleSort(col.key)}
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  flexDirection: 'row-reverse',
                  justifyContent: 'space-between',
                  width: '100%',
                  '& svg': {
                    fontSize: '.75rem',
                  },
                }}
              >
                <Typography variant="caption">{col.label}</Typography>
              </TableSortLabel>
            </Box>
          ))}
        </Stack>

        <Box sx={{ position: 'relative' }}>
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            {(() => {
              const indexMap = new Map();
              visibleNodes.forEach((n, idx) => {
                indexMap.set(n.data.name, idx);
              });

              return visibleNodes.flatMap((node, i) => {
                if (!node.expanded || !node.children) return [];

                const parentX = node.depth * lineIndent * 8;
                const parentY = (i + 0.5) * rowHeight;

                return node.children
                  .filter((child) => indexMap.has(child.data.name))
                  .map((child) => {
                    const j = indexMap.get(child.data.name);
                    const childX = child.depth * lineIndent * 8;
                    const childY = (j + 0.5) * rowHeight;

                    return (
                      <path
                        key={`line-${node.data.name}-${child.data.name}`}
                        d={`
                                                M${parentX},${parentY}
                                                L${parentX},${childY}
                                                L${childX},${childY}
                                            `}
                        fill="none"
                        stroke="#e0e0e0"
                        strokeWidth={1}
                      />
                    );
                  });
              });
            })()}
          </svg>

          {visibleNodes.map((node, i) => (
            <Box
              key={node.data.name}
              sx={{
                maxHeight: node.isVisible ? '100px' : '0px',
                opacity: node.isVisible ? 1 : 0,
                overflow: 'hidden',
                transition: 'max-height 250ms ease, opacity 250ms ease',
                pointerEvents: node.isVisible ? 'auto' : 'none',
              }}
            >
              <Box sx={{ position: 'relative', height: rowHeight }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    height: rowHeight,
                    position: 'relative',
                  }}
                >
                  <Box sx={{ flexGrow: 1, pl: node.depth * indent }}>
                    {node.children || node._children ? (
                      <IconButton
                        disableFocusRipple={true}
                        disableRipple={true}
                        size="small"
                        sx={{ marginLeft: '.5rem' }}
                        onClick={() => toggleNode(node)}
                      >
                        {node.expanded ? (
                          <ExpandMore fontSize="small" />
                        ) : (
                          <ChevronRight fontSize="small" />
                        )}
                      </IconButton>
                    ) : null}
                    <Chip
                      size="small"
                      color={
                        node.data.prodType === 'IMO'
                          ? 'primary'
                          : node.data.prodType === 'AGT'
                            ? 'secondary'
                            : 'warning'
                      }
                      variant="outlined"
                      label={node.data.prodType}
                      sx={{ fontSize: '.7rem', margin: '0 .5rem' }}
                    />
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ verticalAlign: 'middle', textTransform: 'capitalize' }}
                    >
                      {node.data.name.replace(
                        /^([^:]+):(.+)$/,
                        (_, before, after) => before.toUpperCase() + ':' + after.toLowerCase()
                      )}
                    </Typography>
                    {node.data.contractStatus === 'Active' ? (
                      <Tooltip title="View Profile">
                        <Link
                          to={`/MyProfile/Demographic/${node.data.name.split(':')[0].trim()}`}
                          state={{ prevLocation: location.pathname + location.search }}
                        >
                          <IconButton sx={{ marginLeft: '.5rem' }}>
                            <AccountCircle fontSize="small" />
                          </IconButton>
                        </Link>
                      </Tooltip>
                    ) : null}
                  </Box>

                  {columns.map((col) => (
                    <Box
                      key={col.key}
                      sx={{
                        width: col.width,
                        padding: '0 .5rem',
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Chip
                        color={node.data.contractStatus === 'Active' ? 'success' : 'warning'}
                        variant="outlined"
                        label={node.data.contractStatus}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default StatusReportsTree;
